import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownLeft, ArrowUpRight, CloudOff, RefreshCw } from 'lucide-react';
import { Button, Card, EmptyState, MoneyText, Skeleton } from '@/ui';
import type { DisplayRate } from '@/lib/rate-mapping';
import { useSessionStore } from '@/auth/session.store';
import { CreateTaskModal } from '@/features/tasks/CreateTaskModal';
import { CurrencyFlag } from './CurrencyFlag';
import { useRates } from './useRates';
import styles from './Rates.module.css';

/**
 * KUR EKRANI — her iki bolgenin de acilis sayfasi.
 *
 * MUSTERIDE her fiyat hucresi bir DUGME: talep akisi buradan basliyor
 * (kur karti -> tutar -> talep). BUROda ayni hucre dokunulamaz bir bilgi
 * kutusu — duzen degismez, yalniz etkilesim duser. Buro icin teklif akisi
 * Piyasa ekranindan yuruyor.
 *
 * Salt okunurluk PROP DEGIL, OTURUMDAN turetiliyor: bu bir rota ozelligi degil
 * kullanici turunun ozelligi. Prop olsaydi ayni bilesen iki farkli sarmalayici
 * gerektirir, `screens.ts` de JSX tasimak zorunda kalirdi.
 *
 * ALIS / SATIS ETIKETI KULLANICININ BAKISIYLA yazili; cevirim tek bir yerde,
 * src/lib/rate-mapping.ts icinde (banka alani adlari BANKANIN bakisiyla).
 */

export function RatesPage() {
  const { t } = useTranslation();
  const readOnly = useSessionStore((state) => state.snapshot?.userType) === 'exchange';
  const { rows, bulletin, isLoading, isError, refetch } = useRates();
  const [pending, setPending] = useState<{ row: DisplayRate; isBuying: boolean } | null>(null);

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('rates.title')}</h1>
        <div className={styles.meta}>
          {bulletin?.bulletinDate ? (
            <span className={styles.bulletin}>
              {t('rates.bulletinDate', { date: bulletin.bulletinDate })}
            </span>
          ) : null}
          <Button
            tone="ghost"
            size="sm"
            iconLeft={<RefreshCw size={14} aria-hidden="true" />}
            onClick={refetch}
          >
            {t('offerFlow.refresh')}
          </Button>
        </div>
      </header>

      {/* BAYATLIK GIZLENMEZ. Sunucu TCMB'ye ulasamadiginda son bilinen bulteni
          donuyor; kullanicinin bunu bilmeden fiyat konusmasi, hatanin en pahali
          bicimi olurdu. Eski TARIHLI bir bulten (hafta sonu) bayat DEGILDIR. */}
      {bulletin?.stale ? (
        <p className={styles.stale} role="status">
          <CloudOff size={16} aria-hidden="true" />
          {t('rates.stale')}
        </p>
      ) : null}

      {isLoading ? (
        <div className={styles.list}>
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} shape="block" height="96px" />
          ))}
        </div>
      ) : isError ? (
        <Card padding="lg">
          <EmptyState
            tone="error"
            title={t('rates.unavailable')}
            description={t('rates.unavailableDetail')}
            action={
              <Button tone="neutral" onClick={refetch}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card padding="lg">
          <EmptyState title={t('rates.unavailable')} description={t('rates.unavailableDetail')} />
        </Card>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => (
            <li key={row.key}>
              <RateRow
                row={row}
                readOnly={readOnly}
                onPick={(isBuying) => {
                  setPending({ row, isBuying });
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {pending ? (
        <CreateTaskModal
          open
          onClose={() => {
            setPending(null);
          }}
          taskTypeCode={pending.row.taskTypeCode}
          currencyName={pending.row.name}
          isBuying={pending.isBuying}
        />
      ) : null}
    </section>
  );
}

type RateRowProps = {
  row: DisplayRate;
  readOnly: boolean;
  onPick: (isBuying: boolean) => void;
};

function RateRow({ row, readOnly, onPick }: RateRowProps) {
  const { t } = useTranslation();

  const quote = (isBuying: boolean) => {
    const Icon = isBuying ? ArrowDownLeft : ArrowUpRight;
    const label = t(isBuying ? 'labels.buy' : 'labels.sell');
    const body = (
      <>
        <span className={[styles.quoteHead, isBuying ? styles.buy : styles.sell].join(' ')}>
          <Icon size={12} strokeWidth={2.6} aria-hidden="true" />
          {label}
        </span>
        <MoneyText rate={isBuying ? row.buy : row.sell} currency={row.quoteCurrency} size="h3" />
      </>
    );

    if (readOnly) {
      return <div className={styles.quote}>{body}</div>;
    }
    return (
      <button
        type="button"
        className={[styles.quote, styles.quoteAction].join(' ')}
        aria-label={`${row.pairLabel} ${label}`}
        onClick={() => {
          onPick(isBuying);
        }}
      >
        {body}
      </button>
    );
  };

  return (
    <article className={styles.card}>
      <div className={styles.identity}>
        <span className={styles.code}>
          <CurrencyFlag code={row.baseCurrency} />
        </span>
        <div className={styles.names}>
          <span className={styles.pair}>{row.pairLabel}</span>
          <span className={styles.name}>{row.name}</span>
        </div>
        {/* TCMB bazi kurlari 100 birim uzerinden kote ediyor. Fiyatlar 1 birime
            indirgendi; kotasyon birimi yine de soylenir ki tabloyu TCMB
            sayfasiyla karsilastiran kullanici farki anlasin. */}
        {row.unit !== 1 ? (
          <span className={styles.unit}>{t('rates.quotedPer', { unit: row.unit })}</span>
        ) : null}
      </div>

      <div className={styles.quotes}>
        {quote(true)}
        {quote(false)}
      </div>
    </article>
  );
}
